import React from 'react';
import { Book, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

interface ReadingProgressBookshelfProps {
  booksRead: number;
  targetBooks: number;
  year: number;
  onClick?: () => void;
  footerContent?: React.ReactNode;
}

export function ReadingProgressBookshelf({ booksRead, targetBooks, year, onClick, footerContent }: ReadingProgressBookshelfProps) {
  const progressPercent = Math.min((booksRead / targetBooks) * 100, 100);
  const remainingBooks = Math.max(targetBooks - booksRead, 0);
  
  // Determine number of shelves needed (max 10 books per shelf)
  const booksPerShelf = 10;
  // Compact: limit to a single shelf row on dashboard
  const numShelves = 1;
  const showSummary = targetBooks > 50;

  // Brand colors for book spines (Dino palette)
  const bookColors = [
    'bg-[#FFE08A]', // Butter yellow
    'bg-[#6B8E23]', // Avocado green
    'bg-[#8E3B5A]', // Plum
    'bg-[#FAE79F]', // Light butter yellow
    'bg-[#98B06F]', // Light avocado
    'bg-[#A85A7A]', // Light plum
  ];

  // Generate book spines with random heights and colors
  const generateBookSpines = () => {
    const spines = [];
    for (let i = 0; i < Math.min(targetBooks, 50); i++) {
      spines.push({
        filled: i < booksRead,
        color: bookColors[i % bookColors.length],
        height: 80 + (i % 5) * 8, // Varying heights: 80-112px
        index: i
      });
    }
    return spines;
  };

  const bookSpines = generateBookSpines();

  // Organize books into shelves
  const shelves = Array.from({ length: numShelves }, (_, shelfIndex) => {
    const startIdx = shelfIndex * booksPerShelf;
    const endIdx = Math.min(startIdx + booksPerShelf, bookSpines.length);
    return bookSpines.slice(startIdx, endIdx);
  });

  return (
    <Card 
      className={`card-elevated group ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          {year} Reading Goal
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Progress Stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={booksRead >= targetBooks ? "default" : "secondary"}>
              {booksRead} of {targetBooks} books
            </Badge>
            {booksRead >= targetBooks && (
              <Badge variant="default" className="bg-green-500">
                Goal Achieved! 🎉
              </Badge>
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            {progressPercent.toFixed(0)}% complete
          </span>
        </div>

        {/* Progress Bar */}
        <Progress value={progressPercent} className="h-2" />

        {/* Visual Bookshelf */}
        {showSummary ? (
          <div className="text-center p-4 bg-muted/20 rounded-lg">
            <Book className="w-8 h-8 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium">
              {booksRead} books completed
            </p>
            {remainingBooks > 0 && (
              <p className="text-xs text-muted-foreground">
                {remainingBooks} books remaining
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-sm font-medium mb-2">Your Reading Shelf:</p>
            
            {/* Bookshelf with multiple shelves */}
            <div className="space-y-4">
              {shelves.map((shelfBooks, shelfIndex) => (
                <div key={shelfIndex} className="relative">
                  {/* Books on this shelf */}
                  <div className="flex items-end justify-start gap-1 pb-2 min-h-[90px]">
                    {shelfBooks.map((book) => (
                      <div
                        key={book.index}
                        className={`relative group transition-all duration-500 animate-fade-in ${
                          book.filled ? 'opacity-100' : 'opacity-30'
                        }`}
                        style={{
                          width: '28px',
                          height: `${book.height}px`,
                          animationDelay: `${book.index * 50}ms`
                        }}
                      >
                        {/* Book spine */}
                        <div
                          className={`h-full w-full rounded-sm shadow-md transition-transform duration-200 group-hover:scale-105 ${
                            book.filled
                              ? book.color
                              : 'bg-muted/20 border-2 border-dashed border-muted'
                          }`}
                        >
                          {book.filled && (
                            <>
                              {/* Book spine details */}
                              <div className="h-full w-full flex flex-col items-center justify-center p-1 gap-1">
                                <div className="w-3 h-1 bg-white/40 rounded-full" />
                                <div className="w-4 h-0.5 bg-white/30 rounded-full" />
                                <div className="w-3 h-0.5 bg-white/30 rounded-full" />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Shelf plank */}
                  <div className="h-2 w-full bg-gradient-to-b from-[#3d5a3d] to-[#2d4a2d] rounded-sm shadow-md" />
                </div>
              ))}
            </div>

            {remainingBooks > 0 && (
              <p className="text-xs text-muted-foreground text-center mt-4">
                {remainingBooks} more book{remainingBooks !== 1 ? 's' : ''} to reach your goal
              </p>
            )}
          </div>
        )}
        
        {/* Footer content */}
        {footerContent}
      </CardContent>
    </Card>
  );
}